/* Overview — the app at a glance: a live stats strip + the read-only app
 * document (APP.md pointer facts) + the dependency-flow visual. VIEW-ONLY:
 * no JSON editing. The journeys shown are LIVE (from the journey list), so a
 * newly added journey appears here on next view without editing the app doc. */
import React, {
    useEffect,
    useState
} from 'react';
import {
    appEngineService
} from '../api/appEngine.service';
import {
    useAppStudio
} from '../AppStudioContext';
import AppDocCard from '../shared/visuals/AppDocCard';
import DependencyFlow from '../shared/visuals/DependencyFlow';

interface Counts {
    sources: number | null;
    workflows: number | null;
    journeys: number | null;
    roles: number | null;
    users: number | null;
}

const EMPTY_COUNTS: Counts = {
    sources: null,
    workflows: null,
    journeys: null,
    roles: null,
    users: null
};

export default function OverviewPage() {
    const {
        tenancy,
        appId
    } = useAppStudio();
    const [counts, setCounts] = useState<Counts>(EMPTY_COUNTS);
    const [journeyIds, setJourneyIds] = useState<string[]>([]);
    const [appDocText, setAppDocText] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        const safe = <T,>(p: Promise<T>, fb: T): Promise<T> => p.catch(() => fb);
        Promise.all([
            safe(appEngineService.listDefs(tenancy, 'source'), [] as any[]),
            safe(appEngineService.listDefs(tenancy, 'workflow'), [] as any[]),
            safe(appEngineService.listDefs(tenancy, 'journey'), [] as any[]),
            safe(appEngineService.listRoles(tenancy), [] as any[]),
            safe(appEngineService.listUsers(tenancy), [] as any[])
        ]).then(([sources, workflows, journeys, roles, users]) => {
            if (!alive) return;
            setCounts({
                sources: sources.length,
                workflows: workflows.length,
                journeys: journeys.length,
                roles: roles.length,
                users: users.length
            });
            setJourneyIds(journeys.map((j: any) => String(j.id)));
        });
        // Fetch the app doc once (read-only): resolve its id from the defs
        // list when present, else the appId.
        appEngineService.listDefs(tenancy, 'app')
            .then((items) => items[0]?.id || appId)
            .catch(() => appId)
            .then((id) => appEngineService.getDef(tenancy, 'app', id))
            .then((doc) => {
                if (alive) setAppDocText(JSON.stringify(doc, null, 2));
            })
            .catch(() => {
                if (alive) setAppDocText('{}');
            });
        return () => {
            alive = false;
        };
    }, [tenancy, appId]);

    const stats: Array<[string, number | null]> = [
        ['Sources', counts.sources],
        ['Workflows', counts.workflows],
        ['Journeys', counts.journeys],
        ['Roles', counts.roles],
        ['Users', counts.users]
    ];

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '20px 20px 24px'
        }}>
            <div className="aps-page-head" style={{
                marginBottom: 14
            }}>
                <div className="aps-page-head-text">
                    <h1 className="aps-h2">Overview</h1>
                    <p className="aps-page-sub">
                        The app document — eight pointer facts the shell (session,
                        entry, menu, gate) reads. Read-only; the parts below are
                        built in their own sections.
                    </p>
                </div>
            </div>
            <div className="aps-stat-strip">
                {stats.map(([label, n]) => (
                    <div key={label} className="aps-stat">
                        <div className="aps-stat-num">{n == null ? '—' : n}</div>
                        <div className="aps-stat-label">{label}</div>
                    </div>
                ))}
            </div>
            {appDocText != null && (
                <div style={{
                    marginTop: 16
                }}>
                    <AppDocCard docText={appDocText} journeys={journeyIds} />
                    <div style={{
                        padding: '16px 0 0'
                    }}>
                        <DependencyFlow
                            sources={counts.sources}
                            workflows={counts.workflows}
                            journeys={counts.journeys}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
